import {
  conditionValueKind,
  PRIORITY_OPTIONS,
  TYPE_OPTIONS,
  STATUS_CATEGORY_OPTIONS,
} from '@hygge/shared';
import type { ConditionDraft } from '@hygge/shared';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusPicker } from './pickers/status-picker';
import { MemberPicker } from './pickers/member-picker';
import { TagPicker } from './pickers/tag-picker';

const ENUM_OPTIONS: Record<string, readonly { value: string; label: string }[]> = {
  priority: PRIORITY_OPTIONS,
  type: TYPE_OPTIONS,
  statusCategory: STATUS_CATEGORY_OPTIONS,
};

export function ValueEditor({
  boardId,
  condition,
  onChange,
}: {
  boardId: string;
  condition: ConditionDraft;
  onChange: (value: unknown) => void;
}) {
  // When field is unset (draft), no value control makes sense yet
  if (!condition.field) return null;

  const kind = conditionValueKind(condition.field, condition.operator);
  const value = condition.value;

  switch (kind) {
    case 'none':
      return null;
    case 'status':
      return <StatusPicker boardId={boardId} value={(value as string) ?? ''} onChange={onChange} />;
    case 'member':
      return <MemberPicker boardId={boardId} value={value as string} onChange={onChange} />;
    case 'tag':
      return <TagPicker boardId={boardId} value={(value as string) ?? ''} onChange={onChange} />;
    case 'enum': {
      const opts = ENUM_OPTIONS[condition.field] ?? [];
      return (
        <Select value={(value as string) ?? ''} onValueChange={onChange}>
          <SelectTrigger className="h-9 flex-1" aria-label="Value">
            <SelectValue placeholder="Pick a value…" />
          </SelectTrigger>
          <SelectContent>
            {opts.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    case 'number':
    case 'days':
      return (
        <Input
          type="number"
          className="h-9 w-28"
          placeholder={kind === 'days' ? 'days' : 'number'}
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      );
    case 'boolean':
      return (
        <Select
          value={value === false ? 'false' : 'true'}
          onValueChange={(v) => onChange(v === 'true')}
        >
          <SelectTrigger className="h-9 w-28" aria-label="Value">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );
    case 'text':
    default:
      return (
        <Input
          className="h-9 flex-1"
          placeholder="Value"
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
